import AdswizzReportType from '@/Enums/Adswizz/ReportType.enum';
import { ApiTabIndex } from '@/Enums/ReportingDashboard/ApiTabIndex.enum';
import type { IBaseDataReport } from '@/Interfaces/ReportingDashboard/Adswizz/Report.interface';
import type { IAbortSignalThunkArg } from '@/Interfaces/Slices/AsyncThunk.interface';
import { ADSWIZZ_REPORT_ENDPOINT } from '@/constants/endpoints';
import type { RootState } from '@/store';
import {
  selectReportingDashboardCurrentApiTabClients,
  selectReportingDashboardCurrentApiTabWhitelabels,
} from '@/store/selectors/reportingDashboard/shared.selectors';
import type { AsyncThunkPayloadCreator } from '@reduxjs/toolkit';
import axios, { AxiosError, HttpStatusCode } from 'axios';
import { getAdswizzReportingFilter, getEffectiveFilters } from './filters';

const MAX_RETRIES = 1;

export async function fetchAdswizzReport<TBody = any, TReport = any, TFallback = any>({
  signal,
  body,
  maxRetries = MAX_RETRIES,
  url = ADSWIZZ_REPORT_ENDPOINT,
  fallbackValue,
}: {
  signal?: AbortSignal;
  body: TBody;
  maxRetries?: number;
  url?: string;
  fallbackValue?: TFallback;
}): Promise<TReport | TFallback> {
  let reportResponse;
  let attemptRetry = false;
  let retryCounter = 0;
  do {
    try {
      reportResponse = await axios.post(url, body, { signal });
      attemptRetry = false;
    } catch (error) {
      if ((error as AxiosError)?.response?.status !== HttpStatusCode.NotFound) {
        throw error;
      }
      attemptRetry = true;
      retryCounter++;
    }
  } while (retryCounter <= maxRetries && attemptRetry);

  return reportResponse?.data ?? fallbackValue;
}

export const adswizzReportRequestHandler =
  <TReport = IBaseDataReport>(
    reportType: AdswizzReportType,
    fallbackValue: TReport | IBaseDataReport = { report: [] },
  ): AsyncThunkPayloadCreator<TReport, IAbortSignalThunkArg> =>
  async (payload, { getState }) => {
    const state = getState() as RootState;
    const {
      me: { role },
    } = state;

    if (!role) {
      return fallbackValue as TReport;
    }

    const whitelabels = selectReportingDashboardCurrentApiTabWhitelabels(state);
    const clients = selectReportingDashboardCurrentApiTabClients(state);
    const filters = getEffectiveFilters(state, ApiTabIndex.Adswizz);

    const appliedFilter = getAdswizzReportingFilter(filters, role!, clients, whitelabels);

    if (!appliedFilter.advertiserIds?.length && !appliedFilter.campaignIds?.length) {
      return fallbackValue as TReport;
    }

    const response = await fetchAdswizzReport({
      body: { reportType, filters: appliedFilter },
      signal: payload.signal,
      fallbackValue,
    });

    return { ...response, filters } as TReport;
  };
